/**
 * A learner's goals as onboarding and the goal form build them. Pure.
 *
 * The rules mirror the API's (apps/api/src/modules/study-plan/study-plan.goals.ts,
 * `goalProblem`): DTM takes a grade and exactly two subjects, Milliy Sertifikat
 * and languages one or more subjects, IELTS and SAT a level, attestation a
 * category and one or more subjects. The API checks again; these only keep the
 * form from offering a save it would turn down.
 */
import { CUSTOM_TIMEFRAME, DTM_SUBJECT_COUNT, EXAMS, LEVEL_OPTIONS, MAX_GOALS, PRESET_TIMEFRAMES, SUBJECT_OPTIONS } from './constants';
import { addDays, tashkentDay, tashkentMidnightIso, tashkentToday } from './labels';
import type { GoalPlan, GoalRequest, StudyPlan } from './types';

/** One goal while it is being answered. Empty strings are questions not answered yet. */
export interface GoalDraft {
  exam: string;
  level: string;
  subjects: string[];
  timeframe: string;
  /** 'YYYY-MM-DD', for a CUSTOM timeframe. */
  examDate: string;
}

const own = <T>(map: Readonly<Record<string, T>>, key: string): T | undefined =>
  Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;

const TIMEFRAMES: readonly string[] = [...PRESET_TIMEFRAMES, CUSTOM_TIMEFRAME];

export const isOfferedExam = (exam: string): boolean => (EXAMS as readonly string[]).includes(exam);
/** The grades, levels or categories an exam offers; empty for one that takes none. */
export const levelOptions = (exam: string): readonly string[] => own(LEVEL_OPTIONS, exam) ?? [];
/** The subjects or languages an exam offers; empty for one that takes none. */
export const subjectOptions = (exam: string): readonly string[] => own(SUBJECT_OPTIONS, exam) ?? [];
/** How many subjects an exam needs exactly (DTM), or null for "one or more". */
export const exactSubjectCount = (exam: string): number | null => (exam === 'DTM' ? DTM_SUBJECT_COUNT : null);

export const emptyDraft = (exam: string): GoalDraft => ({ exam, level: '', subjects: [], timeframe: '', examDate: '' });

/** Picks or unpicks an exam. The others keep their answers; no more than MAX_GOALS are picked. */
export function toggleExam(drafts: readonly GoalDraft[], exam: string): GoalDraft[] {
  if (drafts.some((draft) => draft.exam === exam)) return drafts.filter((draft) => draft.exam !== exam);
  return drafts.length >= MAX_GOALS ? [...drafts] : [...drafts, emptyDraft(exam)];
}

/** Picks or unpicks a subject. An exam that needs exactly N stops taking more at N. */
export function toggleSubject(draft: GoalDraft, subject: string): GoalDraft {
  if (draft.subjects.includes(subject)) return { ...draft, subjects: draft.subjects.filter((s) => s !== subject) };
  const exact = exactSubjectCount(draft.exam);
  if (exact !== null && draft.subjects.length >= exact) return draft;
  return { ...draft, subjects: [...draft.subjects, subject] };
}

/** Are the exam's own questions (level, subjects) answered with values it offers? */
export function answersComplete(draft: GoalDraft): boolean {
  const levels = levelOptions(draft.exam);
  if (levels.length > 0 && !levels.includes(draft.level)) return false;
  const options = subjectOptions(draft.exam);
  if (options.length === 0) return true;
  const valid = draft.subjects.filter((subject) => options.includes(subject));
  const exact = exactSubjectCount(draft.exam);
  return exact !== null ? valid.length === exact : valid.length > 0;
}

/** The earliest exam day a learner can choose: tomorrow in Tashkent. */
export const earliestExamDay = (today: string = tashkentToday()): string => addDays(today, 1);

/** Is the "when" answered? A chosen date must be after today. */
export function dateComplete(draft: GoalDraft, today: string = tashkentToday()): boolean {
  if (!TIMEFRAMES.includes(draft.timeframe)) return false;
  return draft.timeframe !== CUSTOM_TIMEFRAME || draft.examDate >= earliestExamDay(today);
}

export const goalComplete = (draft: GoalDraft, today?: string): boolean => answersComplete(draft) && dateComplete(draft, today);

/** A goal as the API takes it: only the keys its exam has, since it turns down a level or subjects an exam does not take. */
export function goalRequest(draft: GoalDraft): GoalRequest {
  return {
    exam: draft.exam,
    ...(levelOptions(draft.exam).length > 0 ? { level: draft.level } : {}),
    ...(subjectOptions(draft.exam).length > 0 ? { subjects: [...draft.subjects] } : {}),
    examTimeframe: draft.timeframe,
    ...(draft.timeframe === CUSTOM_TIMEFRAME ? { examDate: tashkentMidnightIso(draft.examDate) } : {}),
  };
}

/**
 * The goal form's starting point from the saved goals. Answers that are not offered (typed into an old form) are
 * left empty so they are asked again. The old combined SAT / IELTS becomes both exams at its level, for the learner
 * to keep the one they mean; an exam the plan does not know is left out for them to choose again.
 */
export function draftsFromPlan(goals: readonly GoalPlan[]): GoalDraft[] {
  const drafts: GoalDraft[] = [];
  const add = (exam: string, goal: GoalPlan) => {
    if (drafts.length >= MAX_GOALS || drafts.some((draft) => draft.exam === exam)) return;
    const levels = levelOptions(exam);
    const options = subjectOptions(exam);
    const exact = exactSubjectCount(exam);
    const subjects = [...new Set(goal.subjects.filter((subject) => options.includes(subject)))];
    const timeframe = goal.examTimeframe && TIMEFRAMES.includes(goal.examTimeframe) ? goal.examTimeframe : '';
    drafts.push({
      exam,
      level: goal.level && levels.includes(goal.level) ? goal.level : '',
      subjects: exact !== null ? subjects.slice(0, exact) : subjects,
      timeframe,
      examDate: timeframe === CUSTOM_TIMEFRAME && goal.examDate ? tashkentDay(goal.examDate) : '',
    });
  };
  for (const goal of goals) {
    if (goal.exam === 'SAT_IELTS') {
      add('IELTS', goal);
      add('SAT', goal);
    } else if (isOfferedExam(goal.exam)) {
      add(goal.exam, goal);
    }
  }
  return drafts;
}

/**
 * The plan's goals. An older server sends one goal in the single-exam shape (`goal`, `roadmap`, `mastery`); it is
 * read as a list of one, with the Milliy Sertifikat subject or the language that shape kept in `gradeOrLevel`.
 */
export function planGoals(plan: StudyPlan): GoalPlan[] {
  if (plan.goals) return plan.goals;
  const { goal } = plan;
  const answerIsSubject = goal.targetExam === 'MILLIY_SERTIFIKAT' || goal.targetExam === 'LANGUAGES';
  return [
    {
      exam: goal.targetExam,
      knownExam: goal.knownExam,
      catalogCategory: goal.catalogCategory,
      catalogSubject: goal.catalogSubject ?? null,
      daysLeft: goal.daysLeft,
      gaps: goal.needsSubjects ? ['subjects'] : [],
      level: answerIsSubject ? null : goal.gradeOrLevel,
      subjects: answerIsSubject ? [goal.gradeOrLevel] : goal.subjects,
      examTimeframe: goal.examTimeframe,
      examDate: goal.examDate,
      roadmap: plan.roadmap,
      mastery: plan.mastery,
    },
  ];
}

/** What every goal shares, from either shape of the plan. */
export const planShared = (plan: StudyPlan): { dailyStudyMinutes: number; targetPercent: number } => ({
  dailyStudyMinutes: plan.dailyStudyMinutes ?? plan.goal.dailyStudyMinutes,
  targetPercent: plan.targetPercent ?? plan.goal.targetPercent,
});
